import { useMemo, useState } from "react";
import type { ComponentType, Dispatch, SetStateAction } from "react";
import { ScrollView, StyleSheet, View } from "react-native";
import type { useTranslation } from "react-i18next";
import {
  buildIntensityGridFromDraft,
  hardSetsByMuscleFromDraft,
  MAX_CYCLE_WEEKS,
  MAX_SESSIONS,
  type Draft,
  type Exercise,
  type UnitsPreference,
} from "@jackedlogs/shared";

import type { ConfirmFn, NotifyFn } from "../../seams";
import type { ExerciseTimerSheetProps, LoggerProfile } from "../../Logger/hostDeps";
import type { useTheme } from "../../Logger/theme";
import { TimelineFocusSection } from "../../Logger/TimelineFocusSection";
import { LoggerModals } from "../../Logger/LoggerModals";
import { IntensityGridView } from "../../IntensityGrid/IntensityGridView";
import { usePrescribeMount } from "./usePrescribeMount";
import { usePrescribePlacement } from "./usePrescribePlacement";
import { CoveragePanel } from "./CoveragePanel";
import { LoadAxisSelector } from "./LoadAxisSelector";
import { PrescribeNoteEditor } from "./PrescribeNoteEditor";
import { Button } from "../../primitives/Button";
import { EditorTopBar } from "../../primitives/EditorTopBar";
import { PrescribeViewTabs, type PrescribeView } from "./PrescribeViewTabs";
import type { ComposerPickerProps } from "../types";

export type PrescribeTreeProps = {
  /** The composer's own live Draft (useProgramEditor/useComposer). */
  programDraft: Draft;
  /** The composer's setDraft — the K2.2 adapter's one write path. */
  setDraft: Dispatch<SetStateAction<Draft>>;
  exerciseCatalog: ReadonlyArray<Exercise> | undefined;
  cellUnit: UnitsPreference;
  notify: NotifyFn;
  confirm: ConfirmFn;
  profile: LoggerProfile;
  t: ReturnType<typeof useTranslation>["t"];
  i18n: ReturnType<typeof useTranslation>["i18n"];
  theme: ReturnType<typeof useTheme>;
  /** PE-24 — the tree bypasses ComposerEditLayout's own chrome, so it needs
   *  its own back/save bar. Reuses the shared EditorTopBar primitive, not a
   *  hand-rolled one — same seams ComposerEditLayout wires (onCancel from
   *  ProgramComposerProps, onSave = the composer's own persist handle). */
  onCancel: () => void;
  onSave: () => void;
  /** PE-26 — structural authoring (Antenore's target flow, point 5: «posso
   *  aggiungere e togliere esercizi, giorni e settimane»). These are
   *  `useComposer`'s own handles, already bound to the SAME `setDraft` this
   *  tree writes cells through, so adding a day here and editing a set here
   *  travel one write path. ⛔ Not re-derived locally: the pure transforms
   *  live in shared (addSession/removeSession/setCycleWeeks/removeWeekAt) and
   *  binding them a second time would be the second copy. */
  addDay: (name: string) => void;
  removeDay: (idx: number) => void;
  changeCycleWeeks: (weeks: number) => void;
  removeWeek: (week: number) => void;
  /** PE-26 — the three COMPONENT slots `<LoggerModals>` requires from its
   *  host (LoggerModals.tsx ~98-100). `Picker` is the same host-wired
   *  Add-Exercise mount the classic composer already threads (types.ts) —
   *  ComposerPickerProps is a Pick of the same ExercisePickerProps
   *  LoggerExercisePickerProps widens, so it slots into ExercisePickerMount
   *  as-is. ExerciseTimerSheetMount/EffortScalePicker are the two new
   *  prescribe-mode-only props on ProgramComposerProps. */
  Picker: ComponentType<ComposerPickerProps>;
  ExerciseTimerSheetMount: ComponentType<ExerciseTimerSheetProps>;
  EffortScalePicker: ComponentType<Record<string, never>>;
};

// PE-22 K2 — the mount for the parallel `prescribe` editor tree (PE-24),
// gated in ProgramComposer.tsx alongside (never inside) ComposerEditLayout.
// Mockup §03 order, all read off the composer's live Draft:
//   1. THE CARD — TimelineFocusSection (itself renders RosterStrip at its
//      own top), reading the draft through the K1 adapter (slotDraftToEntry)
//      and writing it back through the K2.2 draft-direct dispatch adapter.
//   2. IntensityGridView — PE-27: the ONE grid. Heat from
//      buildIntensityGridFromDraft(draft), the day×week cursor, AND the
//      focused exercise's placement toggle via the block-placement seam
//      (usePrescribePlacement, K2.3) — a small, bounded reuse of
//      deriveBlocks/isPlaced/togglePlacement, NOT useComposer's stateful
//      block editor. It used to be two stacked grids; see the component.
//   3. CoveragePanel — read-only, hardSetsByMuscleFromDraft(draft, catalog).
export function PrescribeTree(props: PrescribeTreeProps) {
  const {
    programDraft,
    setDraft,
    exerciseCatalog,
    cellUnit,
    theme,
    t,
    profile,
    confirm,
    notify,
    onCancel,
    onSave,
    addDay,
    removeDay,
    changeCycleWeeks,
    removeWeek,
    Picker,
    ExerciseTimerSheetMount,
    EffortScalePicker,
  } = props;
  const {
    focusSectionProps,
    selectedDay,
    setSelectedDay,
    selectedWeek,
    setSelectedWeek,
    activeExerciseId,
    getExerciseNameBySlug,
    loadAxis,
    onApplyToAllSets,
    timedChip,
    noteChip,
    loggerModalsProps,
  } = usePrescribeMount(props);

  const placementLabel = activeExerciseId
    ? getExerciseNameBySlug(activeExerciseId)
    : "";
  const placement = usePrescribePlacement(
    programDraft,
    setDraft,
    activeExerciseId,
    placementLabel,
  );

  const intensityGrid = useMemo(
    () => buildIntensityGridFromDraft(programDraft),
    [programDraft],
  );

  const hardSetsByMuscle = useMemo(
    () => hardSetsByMuscleFromDraft(programDraft, exerciseCatalog ?? []),
    [programDraft, exerciseCatalog],
  );

  // PE-27 — which of the two tabs is showing. Defaults to Matrice, the leftmost
  // tab: it matches the locked mock (program-editor-mock.html: state.tab starts
  // "matrix") and keeps the default-selected tab aligned with a left-to-right
  // scan. The card and the two read-only grids are mutually exclusive here so
  // the tall card never scrolls past the grids — the defect Antenore censused.
  const [view, setView] = useState<PrescribeView>("matrice");

  return (
    <View style={styles.container}>
      <EditorTopBar
        theme={theme}
        backLabel={t("program.backToList", { defaultValue: "Back to programs" })}
        onBack={onCancel}
        title={programDraft.name || t("program.newTitle", { defaultValue: "New program" })}
        onSave={onSave}
        busy={false}
      />
      {/* PE-27 — the `Matrice · Editor` switcher, pinned below the header with
          the top bar so it stays reachable while either surface scrolls. */}
      <PrescribeViewTabs theme={theme} value={view} onSelect={setView} />
      {/* PE-25 — the tree bypasses ComposerEditLayout, so it must supply its
          own scroll parent like the siblings (ComposerEditLayout.tsx:141):
          without one the content below the card is unreachable and a drag on
          the matrix cell resolves as a press. EditorTopBar stays pinned above. */}
      <ScrollView
        style={styles.body}
        contentContainerStyle={styles.bodyContent}
        keyboardShouldPersistTaps="handled"
      >
        {view === "editor" && (
          <TimelineFocusSection
            {...focusSectionProps}
            // PE-22 K3.2 — «Cosa determina il carico», in the card's expanded
            // band area (mockup §01). null while no set is bound, same as the
            // effort meter it sits beside.
            prescribeAxis={
              loadAxis ? (
                <LoadAxisSelector
                  theme={theme}
                  kind={loadAxis.kind}
                  onSelect={loadAxis.onSelect}
                  cellUnit={cellUnit}
                />
              ) : undefined
            }
            // PE-22 K3.3 — below the band: broadcasts the bound set's whole
            // prescription onto every set of the exercise. null while no set is
            // bound.
            prescribeApplyAll={
              onApplyToAllSets ? (
                <Button
                  theme={theme}
                  variant="ghost"
                  compact
                  label={t("mobile.logger.applyToAllSets", {
                    defaultValue: "Apply to all sets",
                  })}
                  onPress={onApplyToAllSets}
                />
              ) : undefined
            }
            // PE-22 K3.4 — "A tempo": reps⇄time toggle, undefined ⇒ chip hidden.
            onToggleTimed={timedChip?.onToggle}
            timedActive={timedChip?.active}
            // "Nota": the chip's selected state is data-driven (has the row a
            // note?); the editor itself is a slot the chip's local open/close
            // state (owned by FocusPanel, like Burn+'s own bar) reveals.
            noteActive={noteChip?.active}
            prescribeNoteEditor={
              noteChip
                ? (onClose) => (
                    <PrescribeNoteEditor
                      theme={theme}
                      value={noteChip.text}
                      onChange={noteChip.onChange}
                      placeholder={t("program.setNotesPlaceholder", {
                        defaultValue: "Per-set note (optional)",
                      })}
                      onClose={onClose}
                    />
                  )
                : undefined
            }
          />
        )}
        {view === "matrice" && (
          // PE-27 — ONE grid. Until 28/08 this rendered a placement matrix
          // stacked on an intensity grid: the same shape twice, transposed
          // against each other, asking the reader to hold both. The fused grid
          // carries the heat as fill, the placement as a ✓ layer and the cursor
          // as a ring, so the three facts share a cell without sharing ink.
          // ⭐ The caps are the spine's own (ProgramSpine.tsx), so the two
          // surfaces cannot disagree about when the ＋ disappears.
          <IntensityGridView
            grid={intensityGrid}
            selected={selectedDay}
            onSelect={setSelectedDay}
            selectedWeek={selectedWeek}
            onSelectCell={(dayIdx, week) => {
              setSelectedWeek(week);
              setSelectedDay(dayIdx);
            }}
            placement={placement}
            dark={theme.name === "dark"}
            onAddDay={
              programDraft.sessions.length < MAX_SESSIONS
                ? () => addDay(`D${programDraft.sessions.length + 1}`)
                : undefined
            }
            onAddWeek={
              programDraft.cycleWeeks < MAX_CYCLE_WEEKS
                ? () => changeCycleWeeks(programDraft.cycleWeeks + 1)
                : undefined
            }
            onRemoveWeek={removeWeek}
            onRemoveDay={removeDay}
          />
        )}
        {/* PE-27 — coverage is global: it sits below BOTH tabs, never gated by
            the switch, so weekly muscle coverage stays in view whichever
            surface is open. */}
        <CoveragePanel theme={theme} hardSetsByMuscle={hardSetsByMuscle} />
      </ScrollView>
      {/* PE-26 — the same modal stack the real Logger mounts (LoggerScreen.tsx),
          wired onto usePrescribeMount's draft-direct dispatch instead of a live
          session. Un-mounted until now, which is why the card's ＋/swap/tip/
          group/superset taps were silent on the phone. */}
      <LoggerModals
        {...loggerModalsProps}
        theme={theme}
        t={t}
        ExercisePickerMount={Picker}
        ExerciseTimerSheetMount={ExerciseTimerSheetMount}
        EffortScalePicker={EffortScalePicker}
        profile={profile}
        confirm={confirm}
        notify={notify}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1 },
  body: { flex: 1 },
  // Clear the gesture bar so CoveragePanel (last item) isn't flush to the edge,
  // mirroring the sibling ComposerEditLayout's detailContent paddingBottom.
  bodyContent: { paddingBottom: 64 },
});
